import React from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/reducers/userReducer";
import Swal from "sweetalert2";
import { useChatUnreadIndicator } from "../../../hooks/useChatUnreadIndicator";

const SideBarScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector((store) => store.userReducer);

  const { unreadCount: chatUnreadCount, markChatsAsRead } =
    useChatUnreadIndicator(userData);

  const formattedChatBadge = chatUnreadCount > 99 ? "99+" : chatUnreadCount;

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your account",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout"
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(logout());
        navigate("/admin-login");
        Swal.fire({
          title: "Logged out!",
          text: "You have been successfully logged out",
          icon: "success",
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        .sb-wrap {
          display: flex;
          min-height: 100vh;
        }

        /* ── Sidebar ── */
        .sb-sidebar {
          width: 240px;
          min-height: 100vh;
          background: #ffffff;
          border-right: 1px solid #ede9fe;
          display: flex;
          flex-direction: column;
          padding: 0;
          position: fixed;
          top: 0; left: 0;
          z-index: 100;
          box-shadow: 2px 0 16px rgba(99,102,241,0.06);
        }

        /* Logo area */
        .sb-logo-area {
          padding: 28px 24px 20px;
          border-bottom: 1px solid #f1f5f9;
        }
        .sb-logo {
          font-family: 'Syne', sans-serif;
          font-size: 1.55rem;
          font-weight: 800;
          color: #1e1b4b;
          letter-spacing: -0.5px;
          display: flex;
          align-items: center;
          gap: 4px;
          text-decoration: none;
        }
        .sb-logo span { color: #6366f1; }
        .sb-logo-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #6366f1;
          margin-left: 2px;
          animation: sb-pulse 2s ease-in-out infinite;
        }
        @keyframes sb-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .sb-role-badge {
          display: inline-block;
          margin-top: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 10.5px;
          font-weight: 500;
          color: #6366f1;
          background: #ede9fe;
          padding: 2px 10px;
          border-radius: 50px;
          letter-spacing: 0.8px;
          text-transform: uppercase;
        }

        /* Nav section label */
        .sb-section-label {
          font-family: 'DM Sans', sans-serif;
          font-size: 10px;
          font-weight: 600;
          color: #94a3b8;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 20px 24px 8px;
        }

        /* Nav list */
        .sb-nav {
          list-style: none;
          margin: 0;
          padding: 0 12px;
          flex: 1;
        }
        .sb-nav li { margin-bottom: 2px; }

        .sb-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: #475569;
          text-decoration: none;
          transition: background 0.18s, color 0.18s;
          cursor: pointer;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
        }
        .sb-link:hover {
          background: #f0f4ff;
          color: #6366f1;
        }
        .sb-link.active,
        .sb-link:focus {
          background: #ede9fe;
          color: #6366f1;
          font-weight: 600;
        }

        /* Icon wrapper */
        .sb-icon {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: #f1f5f9;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
          transition: background 0.18s;
        }
        .sb-link:hover .sb-icon,
        .sb-link.active .sb-icon {
          background: #ede9fe;
          color: #6366f1;
        }

        /* Badge */
        .sb-badge {
          margin-left: auto;
          background: #ef4444;
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          border-radius: 50px;
          padding: 0 7px;
          height: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
        }

        /* Divider */
        .sb-divider {
          height: 1px;
          background: #f1f5f9;
          margin: 12px 24px;
        }

        /* Logout button at the bottom */
        .sb-bottom {
          padding: 12px;
          border-top: 1px solid #f1f5f9;
        }
        .sb-logout {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: #ef4444;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          transition: background 0.18s;
        }
        .sb-logout:hover { background: #fff1f2; }
        .sb-logout .sb-icon {
          background: #fff1f2;
          color: #ef4444;
        }
        .sb-logout:hover .sb-icon { background: #fee2e2; }

        /* Main content offset */
        .sb-content {
          margin-left: 240px;
          flex: 1;
          min-height: 100vh;
          background: #f8faff;
        }
      `}</style>

      <div className="sb-wrap">
        {/* ── Sidebar ── */}
        <aside className="sb-sidebar">

          {/* Logo */}
          <div className="sb-logo-area">
            <div className="sb-logo">
              AI<span>Hub</span>
              <div className="sb-logo-dot" />
            </div>
            <span className="sb-role-badge">Admin Panel</span>
          </div>

          {/* Nav */}
          <p className="sb-section-label">Main Menu</p>
          <ul className="sb-nav">
            <li>
              <Link to="/admin/admin-dashboard" className="sb-link">
                <span className="sb-icon"><i className="fas fa-home" /></span>
                Dashboard
              </Link>
            </li>
            <li>
              <Link to="/admin/home-directory-manager" className="sb-link">
                <span className="sb-icon"><i className="fas fa-robot" /></span>
                AI Directory
              </Link>
            </li>

            <li>
              <Link to="/admin/ai-tool" className="sb-link">
                <span className="sb-icon"><i className="fas fa-robot" /></span>
                AI Tools
              </Link>
            </li>
            <li>
              <Link to="/admin/blogs" className="sb-link">
                <span className="sb-icon"><i className="fas fa-blog" /></span>
                Blogs
              </Link>
            </li>

            <li>
              <Link to="/admin/edit-profile" className="sb-link">
                <span className="sb-icon"><i className="fa-regular fa-user" /></span>
                Profile
              </Link>
            </li>
          </ul>

          {/* Logout pinned to bottom */}
          <div style={{ flex: 1 }} />
          <div className="sb-bottom">
            <button className="sb-logout" onClick={handleLogout}>
              <span className="sb-icon"><i className="fas fa-sign-out-alt" /></span>
              Logout
            </button>
          </div>

        </aside>

        {/* Page content */}
        <div className="sb-content">
          <Outlet />
        </div>
      </div>
    </>
  );
};

export default SideBarScreen;
